import * as React from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { Button, cn } from '@merels/ui'
import { playerCountBucket, resolveOptions } from '@merels/game-logic/options'
import type { GameSavedOptions } from '@merels/game-logic/types/options'
import { PLATFORM_GAME_OPTIONS } from '@merels/room'
import type { RoomActions } from '../../../deps/room/room.types'
import { useControllerInputDep, useFocusNavigationDep } from '../../providers/FocusNavigationProvider/FocusNavigationProvider'
import { useSignals } from '@preact/signals-react/runtime'
import { useListNavigation } from '../../../hooks/useListNavigation'
import { OptionRow } from './components/OptionRow'
import { BooleanOptionControl } from './components/BooleanOptionControl'
import { NumberOptionControl } from './components/NumberOptionControl'
import { EnumOptionControl } from './components/EnumOptionControl'

function emptySavedGameOptions(): GameSavedOptions {
  return { global: {}, perPlayerCount: {} }
}

export interface GameOptionsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  gameId: string
  savedOptions: GameSavedOptions | null
  roomActions: RoomActions
  gameRegistry: any
  /**
   * Whether Apply should restart the current game.
   * - preGame: only Save
   * - inGame: Save + Apply (restart)
   */
  mode: 'preGame' | 'inGame'
  /** Player counts the user can choose when editing perPlayerCount options. */
  availablePlayerCounts: number[]
  /** Default selected player count when opening. */
  defaultPlayerCount: number
}

function bucketLabel(bucket: number): string {
  if (bucket === 1) return 'a 1-player game'
  if (bucket === 2) return 'a 2-player game'
  if (bucket === 3) return 'a 3-player game'
  return 'a 4+ player game'
}

function uniqueBuckets(counts: number[]): number[] {
  const set = new Set<number>()
  for (const c of counts) {
    const b = playerCountBucket(c)
    if (typeof b === 'number') set.add(b)
  }
  return Array.from(set).sort((a, b) => a - b)
}

export function GameOptionsDialog(props: GameOptionsDialogProps) {
  const {
    open,
    onOpenChange,
    gameId,
    savedOptions,
    roomActions,
    gameRegistry,
    mode,
    availablePlayerCounts,
    defaultPlayerCount,
  } = props

  useSignals()
  const controllerInput = useControllerInputDep()
  const focusNavigation = useFocusNavigationDep()
  const isFocusNavActive = focusNavigation.isActive.value

  const meta = gameRegistry.getGameMetadata(gameId)
  // The platform's own settings (Host plays) follow the game's options.
  const defs = React.useMemo(() => [...(meta?.options ?? []), ...PLATFORM_GAME_OPTIONS], [meta])

  const globalDefs = React.useMemo(() => defs.filter((d: any) => d.scope === 'global'), [defs])
  const perCountDefs = React.useMemo(() => defs.filter((d: any) => d.scope === 'perPlayerCount'), [defs])

  // Helper to get label for an enum value
  const getEnumLabel = React.useCallback((def: any, value: unknown): string => {
    if (def.type !== 'enum') return String(value)
    const choice = def.choices?.find((c: any) => c.value === value)
    return choice?.label ?? String(value)
  }, [])

  const availableBuckets = React.useMemo(() => uniqueBuckets(availablePlayerCounts), [availablePlayerCounts])
  const defaultBucket = React.useMemo(() => playerCountBucket(defaultPlayerCount) ?? 2, [defaultPlayerCount])
  const isVariableScope = availableBuckets.length > 1

  const [selectedBucket, setSelectedBucket] = React.useState<number>(defaultBucket)
  const [draftGlobal, setDraftGlobal] = React.useState<Record<string, unknown>>({})
  const [draftPerCountByCount, setDraftPerCountByCount] = React.useState<Record<number, Record<string, unknown>>>({})

  // Reset drafts when opening or game changes.
  React.useEffect(() => {
    if (!open) return

    const saved = savedOptions ?? emptySavedGameOptions()

    const nextGlobal = resolveOptions(globalDefs, saved.global, { playerCount: undefined })

    const initialBucket =
      Number.isFinite(defaultBucket) && availableBuckets.includes(defaultBucket)
        ? defaultBucket
        : (availableBuckets[availableBuckets.length - 1] || 2)

    const initialPer = resolveOptions(
      perCountDefs,
      saved.perPlayerCount?.[initialBucket] || {},
      { playerCount: initialBucket }
    )

    setSelectedBucket(initialBucket)
    setDraftGlobal(nextGlobal)
    setDraftPerCountByCount({ [initialBucket]: initialPer })
  }, [open, gameId, savedOptions, globalDefs, perCountDefs, defaultBucket, availableBuckets])

  const currentPerCountDraft = draftPerCountByCount[selectedBucket] || {}

  const cycleScope = React.useCallback(() => {
    if (!isVariableScope) return
    const idx = Math.max(0, availableBuckets.indexOf(selectedBucket))
    const next = availableBuckets[(idx + 1) % availableBuckets.length] || selectedBucket
    setDraftPerCountByCount((prev) => {
      const saved = savedOptions ?? emptySavedGameOptions()
      const existingNext = prev[next]
      const resolvedNext = existingNext
        ? existingNext
        : resolveOptions(perCountDefs, saved.perPlayerCount?.[next] || {}, { playerCount: next })
      return { ...prev, [selectedBucket]: currentPerCountDraft, [next]: resolvedNext }
    })
    setSelectedBucket(next)
  }, [isVariableScope, availableBuckets, selectedBucket, currentPerCountDraft, savedOptions, gameId, perCountDefs])

  const hasAnyOptions = globalDefs.length > 0 || perCountDefs.length > 0

  const pillClass =
    "h-9 rounded-md border border-basic bg-background px-3 text-sm text-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-focus"
  const squareClass =
    "h-9 w-9 rounded-md border border-basic bg-background text-sm text-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-focus"

  // Controller support: B/Escape closes.
  React.useEffect(() => {
    if (!open || !isFocusNavActive) return
    const unsub = controllerInput.subscribe((event) => {
      if (event.type === 'cancel' || event.type === 'exit') {
        onOpenChange(false)
      }
    })
    return unsub
  }, [open, isFocusNavActive, controllerInput, onOpenChange])

  const persistDrafts = async () => {
    if (globalDefs.length > 0) {
      await roomActions.setSavedGameOptions({ gameId, scope: 'global', values: draftGlobal })
    }
    if (perCountDefs.length > 0) {
      await roomActions.setSavedGameOptions({
        gameId,
        scope: 'perPlayerCount',
        playerCount: selectedBucket,
        values: currentPerCountDraft,
      })
    }
  }

  const saveAndRestart = async () => {
    if (globalDefs.length > 0 && perCountDefs.length > 0) {
      await roomActions.setSavedGameOptions({ gameId, scope: 'global', values: draftGlobal })
      await roomActions.restartGame({
        options: { scope: 'perPlayerCount' as const, playerCount: selectedBucket, values: currentPerCountDraft },
      })
      return
    }

    if (perCountDefs.length > 0) {
      await roomActions.restartGame({
        options: { scope: 'perPlayerCount' as const, playerCount: selectedBucket, values: currentPerCountDraft },
      })
      return
    }

    await roomActions.restartGame({
      options: { scope: 'global' as const, values: draftGlobal, playerCount: selectedBucket },
    })
  }

  type NavItem =
    | { kind: 'scope'; id: string }
    | { kind: 'toggle'; scope: 'global' | 'perPlayerCount'; id: string }
    | { kind: 'enum'; scope: 'global' | 'perPlayerCount'; id: string; dir: -1 | 1 }
    | { kind: 'number'; scope: 'global' | 'perPlayerCount'; id: string; dir: -1 | 1 }
    | { kind: 'button'; id: 'cancel' | 'save' | 'save-play' | 'save-restart' }

  const navItems: NavItem[] = React.useMemo(() => {
    const items: NavItem[] = []

    if (perCountDefs.length > 0 && isVariableScope) {
      items.push({ kind: 'scope', id: 'change-scope' })
    }

    const pushOptionControls = (defsToRender: typeof defs, scope: 'global' | 'perPlayerCount') => {
      for (const def of defsToRender) {
        if (def.type === 'boolean') {
          items.push({ kind: 'toggle', scope, id: def.id })
        } else if (def.type === 'enum') {
          items.push({ kind: 'enum', scope, id: def.id, dir: -1 })
          items.push({ kind: 'enum', scope, id: def.id, dir: 1 })
        } else if (def.type === 'number') {
          items.push({ kind: 'number', scope, id: def.id, dir: -1 })
          items.push({ kind: 'number', scope, id: def.id, dir: 1 })
        }
      }
    }

    pushOptionControls(globalDefs, 'global')

    const perDefsForBucket = perCountDefs.filter((d: any) => {
      const only = d.appliesWhen?.playerCounts
      return !only || only.length === 0 || only.includes(selectedBucket)
    })
    pushOptionControls(perDefsForBucket, 'perPlayerCount')

    items.push({ kind: 'button', id: 'cancel' })
    if (mode === 'preGame') {
      items.push({ kind: 'button', id: 'save' })
      items.push({ kind: 'button', id: 'save-play' })
    } else {
      items.push({ kind: 'button', id: 'save-restart' })
    }
    return items
  }, [globalDefs, perCountDefs, selectedBucket, mode, isVariableScope])

  const dialogNav = useListNavigation(
    navItems,
    React.useCallback((item) => {
      if (item.kind === 'scope') {
        cycleScope()
        return
      }
      if (item.kind === 'button') {
        if (item.id === 'cancel') onOpenChange(false)
        else if (item.id === 'save') {
          void persistDrafts().then(() => onOpenChange(false))
        } else if (item.id === 'save-play') {
          void persistDrafts()
            .then(() => roomActions.startGameFromSetup())
            .then(() => onOpenChange(false))
        } else {
          void saveAndRestart().then(() => onOpenChange(false))
        }
        return
      }

      const scopeDraft = item.scope === 'global' ? draftGlobal : currentPerCountDraft
      const setScopeDraft =
        item.scope === 'global'
          ? setDraftGlobal
          : (next: Record<string, unknown>) => setDraftPerCountByCount((prev) => ({ ...prev, [selectedBucket]: next }))

      const def = defs.find((d: any) => d.id === item.id && d.scope === item.scope)
      if (!def) return

      if (item.kind === 'toggle') {
        setScopeDraft({ ...scopeDraft, [def.id]: !Boolean(scopeDraft[def.id]) })
        return
      }

      if (item.kind === 'enum' && def.type === 'enum') {
        const values = (def.choices || []).map((c: any) => c.value)
        const current = typeof scopeDraft[def.id] === 'string' ? (scopeDraft[def.id] as string) : String(def.defaultValue)
        const idx = Math.max(0, values.indexOf(current))
        const next = values[(idx + (item.dir === 1 ? 1 : -1) + values.length) % values.length]
        setScopeDraft({ ...scopeDraft, [def.id]: next })
        return
      }

      if (item.kind === 'number' && def.type === 'number') {
        const step = typeof def.step === 'number' && Number.isFinite(def.step) ? def.step : 1
        const current = typeof scopeDraft[def.id] === 'number' ? (scopeDraft[def.id] as number) : def.defaultValue
        const nextRaw = current + step * item.dir
        const next = Math.max(def.min ?? -Infinity, Math.min(def.max ?? Infinity, nextRaw))
        setScopeDraft({ ...scopeDraft, [def.id]: next })
      }
    }, [cycleScope, onOpenChange, roomActions, gameId, persistDrafts, saveAndRestart, draftGlobal, currentPerCountDraft, selectedBucket, defs]),
    open,
    undefined,
    true
  )

  const registerByIndex = React.useCallback(
    (idx: number) => {
      return (el: HTMLButtonElement | null) => {
        if (idx >= 0) dialogNav.registerItem(idx, el)
      }
    },
    [dialogNav]
  )

  const registerToggle = React.useCallback(
    (scope: 'global' | 'perPlayerCount', id: string) =>
      registerByIndex(navItems.findIndex((i) => i.kind === 'toggle' && i.scope === scope && i.id === id)),
    [navItems, registerByIndex]
  )

  const registerNumberButton = React.useCallback(
    (scope: 'global' | 'perPlayerCount', id: string, dir: -1 | 1) =>
      registerByIndex(navItems.findIndex((i) => i.kind === 'number' && i.scope === scope && i.id === id && i.dir === dir)),
    [navItems, registerByIndex]
  )

  const registerEnumButton = React.useCallback(
    (scope: 'global' | 'perPlayerCount', id: string, dir: -1 | 1) =>
      registerByIndex(navItems.findIndex((i) => i.kind === 'enum' && i.scope === scope && i.id === id && i.dir === dir)),
    [navItems, registerByIndex]
  )

  const registerScopeButton = React.useCallback(
    () => registerByIndex(navItems.findIndex((i) => i.kind === 'scope')),
    [navItems, registerByIndex]
  )

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-overlay z-[10000] transition-opacity duration-300 data-[state=open]:opacity-100 data-[state=closed]:opacity-0" />
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-[10000] w-full max-w-xl -translate-x-1/2 -translate-y-1/2 rounded-lg border bg-background p-6 shadow-lg',
            'transition-all duration-300',
            'data-[state=open]:opacity-100 data-[state=closed]:opacity-0',
            'data-[state=open]:scale-100 data-[state=closed]:scale-95'
          )}
        >
          <Dialog.Title className="text-lg font-bold text-primary">Configure {meta?.displayName || gameId}</Dialog.Title>

          {!hasAnyOptions ? (
            <div className="mt-4 text-sm text-muted">This game has no configurable options yet.</div>
          ) : (
            <div className="mt-4 flex flex-col gap-6">
              {globalDefs.length > 0 ? (
                <section className="flex flex-col gap-3">
                  <div className="text-sm font-semibold text-primary">Global</div>
                  <div className="flex flex-col gap-3">
                    {globalDefs.map((def: any) => (
                      <OptionRow
                        key={`global-${def.id}`}
                        label={def.label}
                        description={def.description}
                        control={
                          def.type === 'boolean' ? (
                            <BooleanOptionControl
                              value={Boolean(draftGlobal[def.id])}
                              onToggle={() => setDraftGlobal({ ...draftGlobal, [def.id]: !Boolean(draftGlobal[def.id]) })}
                              buttonClassName={pillClass}
                              registerButton={registerToggle('global', def.id)}
                            />
                          ) : def.type === 'number' ? (
                            <NumberOptionControl
                              valueLabel={typeof draftGlobal[def.id] === 'number' ? String(draftGlobal[def.id]) : String(def.defaultValue)}
                              onDecrement={() => {
                                const step = typeof def.step === 'number' ? def.step : 1
                                const current = typeof draftGlobal[def.id] === 'number' ? (draftGlobal[def.id] as number) : def.defaultValue
                                const next = Math.max(def.min ?? -Infinity, Math.min(def.max ?? Infinity, current - step))
                                setDraftGlobal({ ...draftGlobal, [def.id]: next })
                              }}
                              onIncrement={() => {
                                const step = typeof def.step === 'number' ? def.step : 1
                                const current = typeof draftGlobal[def.id] === 'number' ? (draftGlobal[def.id] as number) : def.defaultValue
                                const next = Math.max(def.min ?? -Infinity, Math.min(def.max ?? Infinity, current + step))
                                setDraftGlobal({ ...draftGlobal, [def.id]: next })
                              }}
                              squareButtonClassName={squareClass}
                              valuePillClassName="h-9 min-w-[52px] rounded-md border border-basic bg-background px-2 text-sm text-primary inline-flex items-center justify-center"
                              registerDecrementButton={registerNumberButton('global', def.id, -1)}
                              registerIncrementButton={registerNumberButton('global', def.id, 1)}
                            />
                          ) : def.type === 'enum' ? (
                            <EnumOptionControl
                              valueLabel={getEnumLabel(def, typeof draftGlobal[def.id] === 'string' ? draftGlobal[def.id] : def.defaultValue)}
                              onPrevious={() => {
                                const values = (def.choices || []).map((c: any) => c.value)
                                const current = typeof draftGlobal[def.id] === 'string' ? (draftGlobal[def.id] as string) : String(def.defaultValue)
                                const idx = Math.max(0, values.indexOf(current))
                                const next = values[(idx - 1 + values.length) % values.length]
                                setDraftGlobal({ ...draftGlobal, [def.id]: next })
                              }}
                              onNext={() => {
                                const values = (def.choices || []).map((c: any) => c.value)
                                const current = typeof draftGlobal[def.id] === 'string' ? (draftGlobal[def.id] as string) : String(def.defaultValue)
                                const idx = Math.max(0, values.indexOf(current))
                                const next = values[(idx + 1) % values.length]
                                setDraftGlobal({ ...draftGlobal, [def.id]: next })
                              }}
                              squareButtonClassName={squareClass}
                              valuePillClassName="h-9 min-w-[52px] rounded-md border border-basic bg-background px-2 text-sm text-primary inline-flex items-center justify-center"
                              registerPreviousButton={registerEnumButton('global', def.id, -1)}
                              registerNextButton={registerEnumButton('global', def.id, 1)}
                            />
                          ) : null
                        }
                      />
                    ))}
                  </div>
                </section>
              ) : null}

              {perCountDefs.length > 0 ? (
                <section className="flex flex-col gap-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-sm font-semibold text-primary">Per player count</div>
                    <div className="flex items-center gap-2">
                      <div className="text-sm text-muted">For {bucketLabel(selectedBucket)}</div>
                      {isVariableScope ? (
                        <button
                          ref={(el) => {
                            registerScopeButton()(el)
                          }}
                          tabIndex={-1}
                          className={pillClass}
                          onClick={cycleScope}
                        >
                          Change scope
                        </button>
                      ) : null}
                    </div>
                  </div>

                  <div className="flex flex-col gap-3">
                    {perCountDefs
                      .filter((d: any) => {
                        const only = d.appliesWhen?.playerCounts
                        return !only || only.length === 0 || only.includes(selectedBucket)
                      })
                      .map((def: any) => (
                        <OptionRow
                          key={`per-${def.id}`}
                          label={def.label}
                          description={def.description}
                          control={
                            def.type === 'boolean' ? (
                              <BooleanOptionControl
                                value={Boolean(currentPerCountDraft[def.id])}
                                onToggle={() => {
                                  setDraftPerCountByCount((prev) => ({
                                    ...prev,
                                    [selectedBucket]: { ...currentPerCountDraft, [def.id]: !Boolean(currentPerCountDraft[def.id]) },
                                  }))
                                }}
                                buttonClassName={pillClass}
                                registerButton={registerToggle('perPlayerCount', def.id)}
                              />
                            ) : def.type === 'number' ? (
                              <NumberOptionControl
                                valueLabel={
                                  typeof currentPerCountDraft[def.id] === 'number'
                                    ? String(currentPerCountDraft[def.id])
                                    : String(def.defaultValue)
                                }
                                onDecrement={() => {
                                  const step = typeof def.step === 'number' ? def.step : 1
                                  const current =
                                    typeof currentPerCountDraft[def.id] === 'number'
                                      ? (currentPerCountDraft[def.id] as number)
                                      : def.defaultValue
                                  const next = Math.max(def.min ?? -Infinity, Math.min(def.max ?? Infinity, current - step))
                                  setDraftPerCountByCount((prev) => ({ ...prev, [selectedBucket]: { ...currentPerCountDraft, [def.id]: next } }))
                                }}
                                onIncrement={() => {
                                  const step = typeof def.step === 'number' ? def.step : 1
                                  const current =
                                    typeof currentPerCountDraft[def.id] === 'number'
                                      ? (currentPerCountDraft[def.id] as number)
                                      : def.defaultValue
                                  const next = Math.max(def.min ?? -Infinity, Math.min(def.max ?? Infinity, current + step))
                                  setDraftPerCountByCount((prev) => ({ ...prev, [selectedBucket]: { ...currentPerCountDraft, [def.id]: next } }))
                                }}
                                squareButtonClassName={squareClass}
                                valuePillClassName="h-9 min-w-[52px] rounded-md border border-basic bg-background px-2 text-sm text-primary inline-flex items-center justify-center"
                                registerDecrementButton={registerNumberButton('perPlayerCount', def.id, -1)}
                                registerIncrementButton={registerNumberButton('perPlayerCount', def.id, 1)}
                              />
                            ) : def.type === 'enum' ? (
                              <EnumOptionControl
                                valueLabel={getEnumLabel(
                                  def,
                                  typeof currentPerCountDraft[def.id] === 'string'
                                    ? currentPerCountDraft[def.id]
                                    : def.defaultValue
                                )}
                                onPrevious={() => {
                                  const values = (def.choices || []).map((c: any) => c.value)
                                  const current =
                                    typeof currentPerCountDraft[def.id] === 'string'
                                      ? (currentPerCountDraft[def.id] as string)
                                      : String(def.defaultValue)
                                  const idx = Math.max(0, values.indexOf(current))
                                  const next = values[(idx - 1 + values.length) % values.length]
                                  setDraftPerCountByCount((prev) => ({ ...prev, [selectedBucket]: { ...currentPerCountDraft, [def.id]: next } }))
                                }}
                                onNext={() => {
                                  const values = (def.choices || []).map((c: any) => c.value)
                                  const current =
                                    typeof currentPerCountDraft[def.id] === 'string'
                                      ? (currentPerCountDraft[def.id] as string)
                                      : String(def.defaultValue)
                                  const idx = Math.max(0, values.indexOf(current))
                                  const next = values[(idx + 1) % values.length]
                                  setDraftPerCountByCount((prev) => ({ ...prev, [selectedBucket]: { ...currentPerCountDraft, [def.id]: next } }))
                                }}
                                squareButtonClassName={squareClass}
                                valuePillClassName="h-9 min-w-[52px] rounded-md border border-basic bg-background px-2 text-sm text-primary inline-flex items-center justify-center"
                                registerPreviousButton={registerEnumButton('perPlayerCount', def.id, -1)}
                                registerNextButton={registerEnumButton('perPlayerCount', def.id, 1)}
                              />
                            ) : null
                          }
                        />
                      ))}
                  </div>
                </section>
              ) : null}
            </div>
          )}

          <div className="mt-6 flex gap-3">
            <Button
              variant="outline"
              className="flex-1"
              ref={(el) => {
                const idx = navItems.findIndex((i) => i.kind === 'button' && i.id === 'cancel')
                if (idx >= 0) dialogNav.registerItem(idx, el)
              }}
              tabIndex={dialogNav.focusedIndex === navItems.findIndex((i) => i.kind === 'button' && i.id === 'cancel') ? 0 : -1}
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            {mode === 'preGame' ? (
              <>
                <Button
                  variant="outline"
                  className="flex-1"
                  ref={(el) => {
                    const idx = navItems.findIndex((i) => i.kind === 'button' && i.id === 'save')
                    if (idx >= 0) dialogNav.registerItem(idx, el)
                  }}
                  tabIndex={dialogNav.focusedIndex === navItems.findIndex((i) => i.kind === 'button' && i.id === 'save') ? 0 : -1}
                  onClick={() => void persistDrafts().then(() => onOpenChange(false))}
                  disabled={!hasAnyOptions}
                >
                  Save
                </Button>
                <Button
                  className="flex-1"
                  ref={(el) => {
                    const idx = navItems.findIndex((i) => i.kind === 'button' && i.id === 'save-play')
                    if (idx >= 0) dialogNav.registerItem(idx, el)
                  }}
                  tabIndex={dialogNav.focusedIndex === navItems.findIndex((i) => i.kind === 'button' && i.id === 'save-play') ? 0 : -1}
                  onClick={() => void persistDrafts().then(async () => await roomActions.startGameFromSetup()).then(() => onOpenChange(false))}
                  disabled={!hasAnyOptions}
                >
                  Save and Play
                </Button>
              </>
            ) : (
              <Button
                className="flex-1"
                ref={(el) => {
                  const idx = navItems.findIndex((i) => i.kind === 'button' && i.id === 'save-restart')
                  if (idx >= 0) dialogNav.registerItem(idx, el)
                }}
                tabIndex={dialogNav.focusedIndex === navItems.findIndex((i) => i.kind === 'button' && i.id === 'save-restart') ? 0 : -1}
                onClick={() => void saveAndRestart().then(() => onOpenChange(false))}
                disabled={!hasAnyOptions}
              >
                Save and Restart
              </Button>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
